// Versão: 1.00
// Data..: 2026-10-13
// TasksView: bloco reutilizável de Tarefas (chamados marcados como isTask), extraído da
// antiga TasksPage para virar aba dentro de Agendamentos (SchedulePage). Lista as tarefas
// (minhas/todas), filtra por status, permite criar/editar tarefas avulsas e adicionar/
// remover a tarefa da agenda Zimbra. Somente equipe. Sem wrapper .full-bleed nem PageHeader
// próprios — a aba já vive dentro do .full-bleed da SchedulePage; o botão "+ Nova tarefa"
// fica no topo deste bloco.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  TicketStatus,
  TicketPriority,
  StatusLabels,
  PriorityLabels,
  RecurrenceFreq,
  RecurrenceFreqLabels,
  type Ticket,
  type Department,
  type TaskRecurrence,
  type Paginated,
} from '@makweb/shared';
import { api } from '../lib/api.js';
import { useToast } from '../context/ToastContext.js';
import { Modal } from '../components/Modal.js';
import { StatusBadge, PriorityBadge } from '../components/Badge.js';

type Scope = 'me' | 'all';
interface Agent { id: string; name: string; }

// Status considerados "abertos" (para o filtro rápido).
const OPEN_STATUSES = new Set<string>([
  TicketStatus.OPEN, TicketStatus.TRIAGE, TicketStatus.IN_PROGRESS,
  TicketStatus.WAITING_CLIENT, TicketStatus.WAITING_THIRD_PARTY, TicketStatus.SCHEDULED,
]);

// Converte ISO em value de <input datetime-local> no fuso local.
function toLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

export function TasksView() {
  const [scope, setScope] = useState<Scope>('all');
  const [tasks, setTasks] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'open' | 'all' | string>('open');
  const [editor, setEditor] = useState<{ task?: Ticket } | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);

  const load = (s: Scope) => {
    setLoading(true);
    api.get<{ items: Ticket[] }>(`/tasks?scope=${s}`)
      .then((r) => setTasks(r.items))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(scope); }, [scope]);
  useEffect(() => {
    api.get<Paginated<Department>>('/departments').then((r) => setDepartments(r.items)).catch(() => {});
  }, []);

  const deptName = (id?: string) => (id ? departments.find((d) => d.id === id)?.name ?? '—' : '—');

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return tasks;
    if (statusFilter === 'open') return tasks.filter((t) => OPEN_STATUSES.has(t.status));
    return tasks.filter((t) => t.status === statusFilter);
  }, [tasks, statusFilter]);

  const statusOptions = useMemo(() => Array.from(new Set(tasks.map((t) => t.status))), [tasks]);

  const afterSave = () => { setEditor(null); load(scope); };

  // Push sob demanda: adiciona/remove a tarefa no calendário Zimbra do responsável.
  const toast = useToast();
  const [calendarBusy, setCalendarBusy] = useState<string | null>(null);
  const addToCalendar = async (t: Ticket) => {
    setCalendarBusy(t.id);
    try {
      await api.post(`/tickets/${t.id}/calendar`);
      toast.success('Tarefa adicionada à agenda');
      load(scope);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao adicionar à agenda');
    } finally {
      setCalendarBusy(null);
    }
  };
  const removeFromCalendar = async (t: Ticket) => {
    setCalendarBusy(t.id);
    try {
      await api.delete(`/tickets/${t.id}/calendar`);
      toast.success('Tarefa removida da agenda');
      load(scope);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover da agenda');
    } finally {
      setCalendarBusy(null);
    }
  };

  return (
    <div>
      {/* Linha de ações da aba: o botão "+ Nova tarefa" vem de dentro do próprio TasksView. */}
      <div className="flex justify-end mb-3">
        <button className="btn btn-primary btn-sm" onClick={() => setEditor({})}>+ Nova tarefa</button>
      </div>

      <div className="flex items-center justify-between mb-4" style={{ flexWrap: 'wrap', gap: 'var(--s2)' }}>
        <div className="flex gap-2">
          <button className={`btn btn-sm ${scope === 'all' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setScope('all')}>✅ Todas ({tasks.length})</button>
          <button className={`btn btn-sm ${scope === 'me' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setScope('me')}>🎯 Minhas</button>
        </div>
        <select className="select input" style={{ maxWidth: 220 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="open">Abertas</option>
          <option value="all">Todos os status</option>
          {statusOptions.map((s) => <option key={s} value={s}>{StatusLabels[s] ?? s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center" style={{ padding: 48 }}><span className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="card empty"><div className="empty-icon">✅</div>Nenhuma tarefa {statusFilter === 'open' ? 'aberta' : ''}. Use "Nova tarefa" para criar uma.</div>
      ) : (
        <div className="card card-flush">
          <table className="table">
            <thead><tr><th>#</th><th>Assunto</th><th>Status</th><th>Prioridade</th><th>Departamento</th><th>Prazo</th><th></th></tr></thead>
            <tbody>
              {filtered.map((t) => {
                const deadline = t.resolutionDueAt ?? t.dueAt;
                const overdue = deadline && new Date(deadline).getTime() < Date.now() && OPEN_STATUSES.has(t.status);
                return (
                  <tr key={t.id}>
                    <td><Link to={`/tickets/${t.id}`}>#{t.number}</Link></td>
                    <td className="font-semibold">
                      {t.subject}
                      {t.recurrence && (
                        <span className="badge badge-normal" style={{ marginLeft: 6 }}
                          title={`Recorrente: ${RecurrenceFreqLabels[t.recurrence.freq] ?? t.recurrence.freq}${t.recurrence.interval && t.recurrence.interval > 1 ? ` (a cada ${t.recurrence.interval})` : ''}`}>
                          🔁 {RecurrenceFreqLabels[t.recurrence.freq] ?? t.recurrence.freq}
                        </span>
                      )}
                    </td>
                    <td><StatusBadge status={t.status} /></td>
                    <td><PriorityBadge priority={t.priority} /></td>
                    <td className="text-muted text-sm">{deptName(t.departmentId)}</td>
                    <td className="text-sm" style={{ color: overdue ? 'var(--danger)' : 'var(--text-2)' }}>
                      {deadline ? new Date(deadline).toLocaleString('pt-BR') : '—'}{overdue ? ' ⚠' : ''}
                    </td>
                    <td className="text-right" style={{ whiteSpace: 'nowrap' }}>
                      {t.caldavUid ? (
                        <>
                          <span className="badge badge-normal" style={{ marginRight: 6 }} title="Esta tarefa está na sua agenda">📅 Na agenda</span>
                          <button className="btn btn-sm btn-ghost" disabled={calendarBusy === t.id} onClick={() => removeFromCalendar(t)}>Remover da agenda</button>
                        </>
                      ) : (
                        <button className="btn btn-sm btn-ghost" disabled={calendarBusy === t.id} onClick={() => addToCalendar(t)}>📅 Adicionar à agenda</button>
                      )}
                      <button className="btn btn-sm btn-ghost" onClick={() => setEditor({ task: t })}>✏️ Editar</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editor && <TaskEditor task={editor.task} departments={departments} onClose={() => setEditor(null)} onSaved={afterSave} />}
    </div>
  );
}

// Modal para criar/editar uma tarefa avulsa (opcionalmente atribuída a um responsável
// e/ou vinculada a um departamento).
function TaskEditor({
  task, departments, onClose, onSaved,
}: {
  task?: Ticket;
  departments: Department[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const isEdit = !!task;
  const [agents, setAgents] = useState<Agent[]>([]);
  const [subject, setSubject] = useState(task?.subject ?? '');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<TicketPriority>(task?.priority ?? TicketPriority.NORMAL);
  const [assigneeId, setAssigneeId] = useState(task?.assigneeId ?? '');
  const [departmentId, setDepartmentId] = useState(task?.departmentId ?? '');
  const [dueAt, setDueAt] = useState(toLocalInput(task?.resolutionDueAt ?? task?.dueAt));
  // Agendamento (data/hora em que a tarefa deve iniciar) e recorrência.
  const [scheduledAt, setScheduledAt] = useState(toLocalInput(task?.scheduledAt));
  const [repeat, setRepeat] = useState<boolean>(!!task?.recurrence);
  const [freq, setFreq] = useState<RecurrenceFreq>(task?.recurrence?.freq ?? RecurrenceFreq.MONTHLY);
  const [interval, setIntervalN] = useState<string>(String(task?.recurrence?.interval ?? 1));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ items: Agent[] }>('/agents').then((r) => setAgents(r.items)).catch(() => {});
  }, []);

  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!subject.trim()) return toast.error('Informe o título da tarefa.');
    setSaving(true);
    const dueIso = dueAt ? new Date(dueAt).toISOString() : undefined;
    const scheduledIso = scheduledAt ? new Date(scheduledAt).toISOString() : undefined;
    // Recorrência: só quando "repetir" está marcado. interval mínimo 1.
    const recurrence: TaskRecurrence | undefined = repeat
      ? { freq, interval: Math.max(1, Number(interval) || 1) }
      : undefined;
    try {
      if (isEdit && task) {
        await api.patch(`/tickets/${task.id}`, {
          subject: subject.trim(),
          priority,
          assigneeId: assigneeId || undefined,
          departmentId: departmentId || undefined,
          dueAt: dueIso,
          scheduledAt: scheduledIso,
          isTask: true,
          // Envia null para limpar a recorrência quando desmarcada.
          recurrence: recurrence ?? (null as unknown as undefined),
        });
        toast.success('Tarefa atualizada');
      } else {
        await api.post('/tickets', {
          subject: subject.trim(),
          body: body.trim() ? `<p>${body.trim()}</p>` : '<p>(tarefa)</p>',
          priority,
          assigneeId: assigneeId || undefined,
          departmentId: departmentId || undefined,
          dueAt: dueIso,
          scheduledAt: scheduledIso,
          isTask: true,
          recurrence,
        });
        toast.success('Tarefa criada');
      }
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao salvar tarefa');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title={isEdit ? `Editar tarefa #${task?.number}` : 'Nova tarefa'}
      onClose={onClose}
      maxWidth={480}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Fechar</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar tarefa'}</button>
        </>
      }
    >
      <form onSubmit={save}>
        <div className="field">
          <label className="label">Título</label>
          <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="O que precisa ser feito" required />
        </div>
        {!isEdit && (
          <div className="field">
            <label className="label">Descrição (opcional)</label>
            <textarea className="textarea" rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Detalhes da tarefa" />
          </div>
        )}
        <div className="field-pair">
          <div className="field mb-0">
            <label className="label">Prioridade</label>
            <select className="select input" value={priority} onChange={(e) => setPriority(e.target.value as TicketPriority)}>
              <option value={TicketPriority.LOW}>{PriorityLabels.low}</option>
              <option value={TicketPriority.NORMAL}>{PriorityLabels.normal}</option>
              <option value={TicketPriority.HIGH}>{PriorityLabels.high}</option>
              <option value={TicketPriority.URGENT}>{PriorityLabels.urgent}</option>
            </select>
          </div>
          <div className="field mb-0">
            <label className="label">Prazo (opcional)</label>
            <input className="input" type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
          </div>
        </div>
        <div className="field-pair mt-3">
          <div className="field mb-0">
            <label className="label">Responsável (opcional)</label>
            <select className="select input" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
              <option value="">— Ninguém —</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="field mb-0">
            <label className="label">Departamento (opcional)</label>
            <select className="select input" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              <option value="">— Nenhum —</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        </div>

        {/* Agendamento: a tarefa fica "agendada" e inicia (vira aberta) na data/hora. */}
        <div className="field mt-3 mb-0">
          <label className="label">Agendar início (opcional)</label>
          <input className="input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
          <p className="text-dim text-xs mt-1" style={{ marginBottom: 0 }}>
            A tarefa fica agendada e é ativada automaticamente na data/hora escolhida.
          </p>
        </div>

        {/* Recorrência: ao concluir a tarefa, o sistema cria a próxima ocorrência. */}
        <label className="flex items-center gap-2 field mt-3 mb-0" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} />
          <span>Repetir (tarefa recorrente)</span>
        </label>
        {repeat && (
          <div className="field-pair mt-2">
            <div className="field mb-0">
              <label className="label">Frequência</label>
              <select className="select input" value={freq} onChange={(e) => setFreq(e.target.value as RecurrenceFreq)}>
                {Object.entries(RecurrenceFreqLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="field mb-0">
              <label className="label">A cada</label>
              <input className="input" type="number" min={1} value={interval} onChange={(e) => setIntervalN(e.target.value)} />
              <p className="text-dim text-xs mt-1" style={{ marginBottom: 0 }}>
                Ex.: 2 + Semanal = a cada 2 semanas.
              </p>
            </div>
          </div>
        )}
        {repeat && (
          <p className="text-dim text-xs mt-2" style={{ marginBottom: 0 }}>
            Ao concluir (resolver/fechar) esta tarefa, a próxima ocorrência é criada
            automaticamente, agendada para a data seguinte.
          </p>
        )}

        <p className="text-dim text-xs mt-3">
          Uma tarefa é um chamado interno. Vincule a um departamento para que a equipe dele acompanhe,
          e/ou a um responsável específico (que recebe alerta).
        </p>
        <button type="submit" style={{ display: 'none' }} />
      </form>
    </Modal>
  );
}
